export type WebsiteRevisionOrigin = 'model-tool' | 'user-undo' | 'user-restore' | 'published-restore' | 'initial-generation' | 'migration'

export interface WebsiteRevisionModelProvenance {
  attemptId: string
  label: string
  spec: string
  maxOutputTokens: number
  outputTokens: number
  role: 'edit' | 'refine'
  completedAt: string
}

export interface WebsiteRevisionSummary {
  revision: number
  parentRevision: number | null
  changeSetId: string
  summary: string
  origin: WebsiteRevisionOrigin
  restoreSourceRevision: number | null
  createdAt: string
  publishedVersions?: number[]
  modelProvenance?: WebsiteRevisionModelProvenance[]
}

export interface WebsiteRevisionRead extends WebsiteRevisionSummary { mode: 'section' | 'import'; html: string; renderHtml?: string }

export interface WebsiteRevisionComparison {
  from: WebsiteRevisionSummary
  to: WebsiteRevisionSummary
  changed: boolean
  modeChanged: boolean
  changedDimensions: string[]
  changedSections: string[]
  siteChromeChanged: boolean
  layoutChanged: boolean
  assetsChanged: boolean
  fromPreview?: { html: string; renderHtml?: string }
  toPreview?: { html: string; renderHtml?: string }
}

export interface WebsiteRevisionMutationResult {
  mutated: boolean
  stale?: boolean
  replayed?: boolean
  revision: number
  previousRevision: number
  changeSetId: string
  html?: string
  renderHtml?: string
}

interface ToolEnvelope<T> { structuredContent?: T & { error?: string }; error?: string; content?: unknown[] }

export class WebsiteBuilderService {
  constructor(private readonly apiBaseUrl: string, private readonly token: string | null) {}

  private async tool<T>(sessionId: string, tool: string, args: Record<string, unknown>): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(`${this.apiBaseUrl}/api/site-builder/tool`, {
      method: 'POST', credentials: 'include', headers,
      body: JSON.stringify({ tool, sessionId, arguments: args }),
    })
    const envelope = await response.json().catch(() => ({})) as ToolEnvelope<T>
    if (!response.ok || !envelope.structuredContent || envelope.structuredContent.error) throw new Error(envelope.structuredContent?.error || envelope.error || `Website Builder request failed (${response.status})`)
    return envelope.structuredContent
  }

  async listRevisions(sessionId: string, options: { limit?: number; beforeRevision?: number } = {}): Promise<WebsiteRevisionSummary[]> {
    const result = await this.tool<{ revisions: WebsiteRevisionSummary[] }>(sessionId, 'list_revisions', options)
    return result.revisions
  }

  readRevision(sessionId: string, revision: number): Promise<WebsiteRevisionRead> {
    return this.tool(sessionId, 'read_revision', { revision })
  }

  compareRevisions(sessionId: string, fromRevision: number, toRevision: number): Promise<WebsiteRevisionComparison> {
    return this.tool(sessionId, 'compare_revisions', { fromRevision, toRevision })
  }

  restoreRevision(sessionId: string, sourceRevision: number, expectedHeadRevision: number, changeSetId: string): Promise<WebsiteRevisionMutationResult> {
    return this.tool(sessionId, 'restore_revision', { sourceRevision, expectedHeadRevision, changeSetId })
  }

  undoRevision(sessionId: string, expectedHeadRevision: number, changeSetId: string): Promise<WebsiteRevisionMutationResult> {
    return this.tool(sessionId, 'undo_revision', { expectedHeadRevision, changeSetId })
  }
}
